// @ts-check
/** @typedef {import('@datahooks/types/tagging.types').TagValueDeleteRequest} TagValueDeleteRequest */
/** @typedef {import('@datahooks/types/tagging.types').TagValueAddRequest} TagValueAddRequest */

import React, { useState, useMemo } from 'react';

import { Dialog, DialogFooter, DialogHeader } from '@material-tailwind/react';
import { IoMdPricetags } from "react-icons/io";

import { useProjectsListContext } from '@datahooks/ProjectsListContext';
import useProjectTagsManager from '@datahooks/admin/project/useProjectTagsManager.js';
import useProjectTagValues from '@datahooks/admin/project/useProjectTagValues.js';

import AlertManager from '@components/general/AlertManager.jsx';
import LoadingIcon from '@components/navigation/LoadingIcon.jsx';
import ProjectTagValues from './ProjectTagValues.jsx';
import ProjectTagsAddDialog from './ProjectTagsAddDialog.jsx';
import { TestableButton, TestableText } from '@components/general/TestableComponents.jsx';

import logError from '@datahooks/metadata/ErrorLogging.js';

import PropTypes from 'prop-types';

const ProjectTagsDialog = ({ projectId }) => {
  // Local state for dialog visibility and feedback messages.
  const [isOpen, setIsOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch lookup and project data
  const { orgTags, isLoading, error } = useProjectsListContext();
  const { projectTagValues, isLoading: isTagValuesLoading, error: tagValuesError, refetch: refetchTagValues } = useProjectTagValues();
  const currentProjectTagValues = projectTagValues?.filter(ptv => ptv.project_id === projectId) || [];

  const hasAssignedValues = useMemo(  // Check if any tags have values assigned
    () => currentProjectTagValues.some(r => Array.isArray(r.tag_value_ids) && r.tag_value_ids.length > 0),
    [currentProjectTagValues]
  );

  // Callbacks for successful or failed notification updates.
  const projectTagsUpdated = () => {
    refetchTagValues();
  };

  const projectTagsSaveFailed = (error) => {
    setErrorMessage("Failed to update tags: " + (error.message || 'Unknown error'));
  };

  // Hook to manage tag updates using optimistic updates.
  const { performAction } = useProjectTagsManager( projectTagsUpdated, projectTagsSaveFailed);

  // Toggle the dialog open/closed.
  const handleToggle = () => setIsOpen(!isOpen);

  // Handle toggling a notification on or off.
  const handleAddProjectTagValue = (tag) => {

    /* @type {TagValueAddRequest} */
    const payload = {
      project_id: tag.project_id,
      tag_value_id: tag.tag_value_id,
    };

    // Call performAction from the notification manager hook.
    performAction(payload)
      .catch((error) => {
        logError("Error adding project tag:", error);
    });
  };

  const removeTagValue = (value) => {
  
    /* @type {TagValueDeleteRequest} */
    const payload = {
      project_id: projectId,
      tag_value_id: value.tag_value_id,
    };

    performAction(payload, 'DELETE')
      .catch((error) => {
        logError("Error updating project tag:", error);
      });
  }

  if (isLoading || isTagValuesLoading) {
    return <LoadingIcon showText />;
  }

  if (error || tagValuesError) {
    return <div>Error: {(error || tagValuesError).message}</div>;
  }

  // Organisation has no tags configured
  if (orgTags.length === 0) {
    return null;
  }

  if (!isOpen) {
    return (
      <TestableButton
        dataTestId="project-notifications-button"
        size="sm"
        variant="outlined"
        className="flex text-blue-500 items-center gap-3"
        onClick={handleToggle}
      >
        <IoMdPricetags data-testid="pnb-icon" size={20} /> Tags
      </TestableButton>
    );
  }

  return (
    <Dialog data-testid="project-notifications-dialogue" open={isOpen} size="lg" handler={handleToggle} dismissible={false}>
      <DialogHeader className="flex items-center gap-3 text-blue-500 pb-0">
        <IoMdPricetags data-testid="pnd-icon" size={24} /> Project Tags
      </DialogHeader>
      <div className="p-4">
          { hasAssignedValues ? (
              orgTags.map((tag) => {
                const currentTagValuesForProject = currentProjectTagValues.filter((ptv) => ptv.tag_slug === tag.tag_slug);
                const tagValueIdsForTag = currentTagValuesForProject.flatMap(ptv => ptv.tag_value_ids);
                if (tagValueIdsForTag.length === 0) return null; // Skip tags with no values assigned

                return (
                <div data-testid={`pnd-notification-container${tag.tag_id}`} key={tag.tag_id} className="flex justify-between items-center py-2 border-b">
                  <div>
                    <div data-testid="pnd-nc-title" className="font-bold flex flex-col gap-2">
                      <TestableText variant='h6' className="text-blue-500" dataTestId={`pnd-nc-title-text`}>{tag.tag_title}</TestableText>
                      <ProjectTagValues
                        tagValues={tag.tag_values.filter(tv => tagValueIdsForTag.includes(tv.tag_value_id))}
                        onRemove={removeTagValue}
                      />
                    </div>
                  </div>
                </div>
                ); 
              })
            ) : (
            <TestableText dataTestId="pnd-no-notifications" className='w-full text-center pt-5'>No project tags set.</TestableText>
          )}

        <AlertManager
          data-testid="pnd-alert-manager"
          className="m-2"
          successMessage={successMessage}
          setSuccessMessage={setSuccessMessage}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />

      </div>

      <DialogFooter data-testid="pnd-footer">

        <ProjectTagsAddDialog
          availableTags={orgTags}
          projectId={projectId}
          existingTags={currentProjectTagValues} 
          onAdd={handleAddProjectTagValue}
        />

        <TestableButton dataTestId="pndf-close-button" className='ml-auto' color="pink" onClick={handleToggle}>
          Close
        </TestableButton>
      </DialogFooter>

    </Dialog>
  );
};

ProjectTagsDialog.propTypes = {
  projectId: PropTypes.number.isRequired,
};

export default ProjectTagsDialog;